import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Key } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layout/AuthLayout';
import { API_URL } from '../../config/api';

function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('email'); // 'email', 'reset', 'success'
  const [error, setError] = useState('');

  const meetsPasswordPolicy = (password) => {
    if (!password) return false;
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    return hasMinLength && hasUppercase && hasSpecial;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError(t('forgotPassword.enterEmailError'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        let data = {};
        try {
          data = await response.json();
        } catch (_e) {}
        const backendMessage = (data && (data.message || data.error || data.detail)) || '';
        throw new Error(backendMessage || t('common.networkError'));
      }

      setStep('reset');
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError(t('common.serverConnectionError'));
      } else {
        setError(err.message);
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!resetCode || resetCode.length !== 6) {
      setError(t('forgotPassword.enterCodeError'));
      return;
    }

    if (!newPassword) {
      setError(t('forgotPassword.enterPasswordError'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('forgotPassword.passwordsDoNotMatch'));
      return;
    }

    if (!meetsPasswordPolicy(newPassword)) {
      setError(t('forgotPassword.passwordRequirements'));
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          code: parseInt(resetCode, 10),
          newPassword: newPassword
        })
      });

      let result = {};
      try {
        result = await response.json();
      } catch (_e) {}

      if (!response.ok) {
        const backendMessage = (result && (result.message || result.error || result.detail)) || '';
        throw new Error(backendMessage || t('common.networkError'));
      }

      setStep('success');
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError(t('common.serverConnectionError'));
      } else {
        setError(err.message);
      }
    }
  };

  // no-op: resend handled server-side during initial request

  // Success step
  if (step === 'success') {
    return (
      <AuthLayout 
        title={t('forgotPassword.successTitle')}
        subtitle={t('forgotPassword.successSubtitle')}
      >
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <Key className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-sm text-gray-600">
            {t('forgotPassword.successMessage')}
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200 space-y-4">
          <Link
            to="/signin"
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-primary hover:bg-blue-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary transition-colors duration-200"
          >
            {t('auth.signIn')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Combined code + password reset step
  if (step === 'reset') {
    return (
      <AuthLayout 
        title={t('forgotPassword.newPassword')}
        subtitle={t('verifyCode.subtitle', { email })}
      >
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
          <form className="space-y-6" onSubmit={handlePasswordSubmit}>
            <div>
              <label htmlFor="resetCode" className="block text-sm font-medium text-gray-700 mb-2">
                {t('forgotPassword.resetCode')}
              </label>
              <input
                id="resetCode"
                name="resetCode"
                type="text"
                required
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary text-center text-lg tracking-widest"
                placeholder={t('forgotPassword.resetCodePlaceholder')}
                maxLength="6"
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                {t('forgotPassword.newPassword')}
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary"
                placeholder={t('forgotPassword.newPasswordPlaceholder')}
              />
              {newPassword && !meetsPasswordPolicy(newPassword) && (
                <p className="mt-1 text-sm text-red-600">
                  {t('forgotPassword.passwordRequirements')}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signUp.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary"
                placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {t('forgotPassword.passwordsDoNotMatch')}
                </p>
              )}
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!resetCode || resetCode.length !== 6 || !newPassword || !confirmPassword}
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-primary hover:bg-blue-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {t('forgotPassword.resetPassword')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </form>

          <div className="mt-6">
            <button
              onClick={() => setStep('email')}
              className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary transition-colors duration-200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('common.back')}
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Email input step (initial step)
  return (
    <AuthLayout 
      title={t('forgotPassword.title')}
      subtitle={t('forgotPassword.subtitle')}
    >
      <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
        <form className="space-y-6" onSubmit={handleEmailSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.email')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary"
              placeholder={t('forgotPassword.emailPlaceholder')}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!email}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-primary hover:bg-blue-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {t('forgotPassword.sendResetCode')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t('forgotPassword.backToSignIn')}{' '}
            <Link
              to="/signin"
              className="font-medium text-blue-primary hover:text-blue-secondary"
            >
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}

export default ForgotPassword; 